import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CompanyFilter } from '../components/CompanyFilter';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { Cog, ArrowRight } from 'lucide-react';

export default function MachineAnalyticsPage() {
  const [data, setData] = useState({ machines: [] });
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [sites, setSites] = useState([]);
  const [filterCompany, setFilterCompany] = useState('all');
  const [filterSite, setFilterSite] = useState('all');

  useEffect(() => {
    api.get('/companies').then(r => setCompanies(r.data)).catch(() => {});
    api.get('/sites').then(r => setSites(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (filterCompany !== 'all') params.company_id = filterCompany;
    if (filterSite !== 'all') params.site_id = filterSite;
    api.get(`/analytics/machine-analysis?${new URLSearchParams(params)}`).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [filterCompany, filterSite]);

  const chartData = (data.machines || []).filter(m => m.cost_per_hour > 0).slice(0, 10).map(m => ({ name: m.serial_number, cph: m.cost_per_hour }));

  return (
    <div data-testid="machine-analytics-page">
      <div className="page-header">
        <div><h1 className="page-title">Machine Analytics</h1><p className="text-sm text-slate-500 mt-1">Compare excavator tooth point cost performance across machines</p></div>
      </div>

      <div className="flex gap-3 mb-6" data-testid="machine-analytics-filters">
        <CompanyFilter companies={companies} value={filterCompany} onChange={setFilterCompany} className="w-52" />
        <Select value={filterSite} onValueChange={setFilterSite}><SelectTrigger className="w-52 bg-white"><SelectValue placeholder="All Sites" /></SelectTrigger><SelectContent><SelectItem value="all">All Sites</SelectItem>{sites.map(s => <SelectItem key={s.site_id} value={s.site_id}>{s.name}</SelectItem>)}</SelectContent></Select>
      </div>

      {chartData.length > 0 && (
        <Card className="stat-card mb-6" data-testid="machine-cph-chart">
          <h3 className="text-sm font-semibold text-slate-900 font-heading mb-4">Top 10 Machines by Cost/Hr</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData} layout="vertical"><CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis type="number" tick={{ fontSize: 11, fill: '#64748B' }} tickFormatter={v => `₹${v}`} /><YAxis dataKey="name" type="category" width={110} tick={{ fontSize: 10, fill: '#334155' }} /><Tooltip formatter={v => `₹${Number(v).toFixed(2)}`} contentStyle={{ border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 12 }} /><Bar dataKey="cph" fill="#0F172A" radius={[0,4,4,0]} name="Cost/Hr" /></BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      <div className="data-table-wrapper">
        <Table>
          <TableHeader><TableRow className="bg-slate-50 border-b border-slate-200">
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Machine</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Serial</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Site</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Total Spend</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Cost/Hr</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Hours</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Products</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Change-outs</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500"></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-400">Loading...</TableCell></TableRow> :
            (data.machines || []).length === 0 ? <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-400">No machine data</TableCell></TableRow> :
            (data.machines || []).map(m => (
              <TableRow key={m.machine_id} className="hover:bg-slate-50/50">
                <TableCell className="font-medium text-slate-900"><div className="flex items-center gap-2"><Cog size={14} className="text-slate-400" />{m.brand} {m.model}</div></TableCell>
                <TableCell className="text-sm text-slate-500">{m.serial_number}</TableCell>
                <TableCell className="text-sm text-slate-500">{m.site}</TableCell>
                <TableCell className="text-sm font-medium">₹{m.total_spend.toLocaleString()}</TableCell>
                <TableCell className="text-sm font-bold text-slate-900">{m.cost_per_hour > 0 ? `₹${m.cost_per_hour.toFixed(2)}` : '-'}</TableCell>
                <TableCell className="text-sm text-slate-600">{m.total_hours.toLocaleString()}h</TableCell>
                <TableCell className="text-sm text-slate-600">{m.product_count}</TableCell>
                <TableCell className="text-sm text-slate-600">{m.change_outs}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Link to={`/analytics/machines/${m.machine_id}`} className="text-xs text-blue-600 hover:underline" data-testid={`drill-machine-${m.serial_number}`}>Detail</Link>
                    <Link to={`/analytics/machines/${m.machine_id}/history`} className="text-xs text-violet-600 hover:underline" data-testid={`history-machine-${m.serial_number}`}>History</Link>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
